import { useEffect, useState, useRef } from 'react';

type Toast = {
  id: number;
  message: string;
  title?: string;
  variant?: 'info' | 'success' | 'error';
  duration?: number;
  actionLabel?: string;
  actionUrl?: string;
  onAction?: () => void;
};

export default function GlobalToast() {
  const [queue, setQueue] = useState<Toast[]>([]);
  const idRef = useRef(1);

  useEffect(() => {
    const handler = (e: any) => {
      try {
        const d = e?.detail ?? {};
        const msg = String(d.message ?? d.msg ?? '');
        if (!msg) return;
        const title = String(d.title ?? '').trim();
        const variant = d.variant || (d.success ? 'success' : d.error ? 'error' : 'info');
        const duration = typeof d.duration === 'number' ? d.duration : 3000;
        const id = idRef.current++;
        setQueue((q) => [...q, { id, title, message: msg, variant, duration, actionLabel: d.actionLabel, actionUrl: d.actionUrl, onAction: d.onAction }]);
      } catch {}
    };
    window.addEventListener('tt:toast', handler as EventListener);
    return () => window.removeEventListener('tt:toast', handler as EventListener);
  }, []);

  useEffect(() => {
    if (queue.length === 0) return;
    const t = queue[0];
    const timer = window.setTimeout(() => {
      setQueue((q) => q.slice(1));
    }, t.duration ?? 3000);
    return () => window.clearTimeout(timer);
  }, [queue]);

  if (queue.length === 0) return null;

  const current = queue[0];

  const bg =
    current.variant === 'success'
      ? 'bg-pink-600 text-white'
      : current.variant === 'error'
        ? 'bg-rose-600 text-white'
        : 'bg-pink-500 text-white';

  return (
    <div className="fixed inset-x-0 top-6 z-60 flex justify-center pointer-events-none px-4">
      <div className={`pointer-events-auto inline-flex items-center gap-3 rounded-lg shadow-lg px-4 py-2 ${bg}`}>
        <div className="min-w-0">
          {current.title && <div className="text-sm font-semibold">{current.title}</div>}
          <div className="text-sm">{current.message}</div>
        </div>
        {current.actionLabel && (
          <button
            onClick={() => {
              try {
                if (typeof current.onAction === 'function') {
                  current.onAction();
                  setQueue((q) => q.filter((toast) => toast.id !== current.id));
                  return;
                }
                // Prefer in-app navigation helper when available
                const nav = (window as any).__TT_navigate;
                if (typeof nav === 'function') nav(current.actionLabel === 'Rejected' ? 'Import Rejections' : current.actionLabel);
                else if (current.actionUrl) window.location.href = current.actionUrl;
                else if (current.actionLabel === 'Rejected') window.location.href = '/import/rejections';
              } catch {}
            }}
            className="ml-3 inline-flex items-center px-3 py-1.5 rounded-md bg-white/10 text-white text-xs font-medium hover:bg-white/20"
          >
            {current.actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}
